import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, Eye, EyeOff, CheckCircle, XCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const LOGO_URL = "/images/singleseen_logo_transparent.png";

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams();
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("form"); // form, success, error

  useEffect(() => {
    if (!token) {
      setStatus("error");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error(t('register.passwordsNoMatch'));
      return;
    }

    if (password.length < 6) {
      toast.error(t('register.passwordTooShort'));
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setStatus("success");
    } catch (error) {
      if (error.response?.data?.detail?.includes("expired")) {
        toast.error(t('resetPassword.expiredError'));
      } else {
        toast.error(error.response?.data?.detail || "Error resetting password");
      }
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  if (status === "success") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-rose-100">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="font-serif text-2xl text-rose-900 mb-2">{t('resetPassword.successTitle')}</h1>
          <p className="text-rose-600 mb-8">
            {t('resetPassword.successDesc')}
          </p>
          <Button
            onClick={() => navigate("/login")}
            className="w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-full"
            data-testid="go-to-login-btn"
          >
            {t('common.goToLogin')}
          </Button>
        </div>
      </div>
    );
  }

  if (status === "error" && !token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-rose-100">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="font-serif text-2xl text-rose-900 mb-2">{t('resetPassword.invalidTitle')}</h1>
          <p className="text-rose-600 mb-8">
            {t('resetPassword.invalidDesc')}
          </p>
          <Button
            onClick={() => navigate("/forgot-password")}
            className="w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-full"
          >
            {t('resetPassword.requestNewLink')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="flex items-center justify-center mb-8">
          <img src={LOGO_URL} alt="SingleSeen" className="h-24 w-auto" />
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-rose-100">
          <h1 className="font-serif text-2xl text-rose-900 mb-2 text-center">{t('resetPassword.title')}</h1>
          <p className="text-rose-600 mb-8 text-center">
            {t('resetPassword.subtitle')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-rose-700">{t('resetPassword.newPassword')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-rose-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('resetPassword.newPasswordPlaceholder')}
                  className="pl-10 pr-10 bg-white border-rose-200 focus:border-rose-500 h-12"
                  required
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-rose-400 hover:text-rose-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-rose-700">{t('resetPassword.confirmNewPassword')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-rose-400" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('resetPassword.confirmPlaceholder')}
                  className="pl-10 bg-white border-rose-200 focus:border-rose-500 h-12"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 rounded-full h-12 text-lg font-medium"
              data-testid="reset-submit-btn"
            >
              {loading ? t('common.resetting') : t('resetPassword.resetButton')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
